import React, { useState, useEffect } from "react";
import { Box, Typography, Dialog, IconButton } from "@mui/material";
import toast from "react-hot-toast";
import { apiCall, getJsonResponse } from '../utils/apiUtils';
import { getToken } from '../utils/cookieUtils';

export default function DeletePaymentMethod({
  isPaymentLinked,
  paymentAccountDetails,
  syncStatusDetails,
  className,
  style,
  onPaymentDeleted,
}) {
  const userId = localStorage.getItem("logged_user_id");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  // Show warning if account is not fully ready (based on syncStatusDetails or fallback)
  useEffect(() => {
    if (!paymentAccountDetails || paymentAccountDetails.status !== true) {
      setShowWarningDialog(false);
      return;
    }

    let shouldWarn = false;
    let message = "";

    if (syncStatusDetails && syncStatusDetails.success) {
      const { primary_email_confirmed, payments_receivable } = syncStatusDetails;
      if (!primary_email_confirmed) {
        shouldWarn = true;
        message =
          "Your PayPal email is not yet confirmed. Deleting this payment method will require you to set up a new PayPal account connection.";
      } else if (!payments_receivable) {
        shouldWarn = true;
        message =
          "Your PayPal account is not fully set up to receive payments. Deleting this payment method will require you to set up a new PayPal account connection.";
      }
    } else {
      const emailStatus = String(paymentAccountDetails.emailStatus || "")
        .replace(/[\s_]/g, "")
        .toUpperCase();
      if (emailStatus === "NOTCONFIRMED") {
        shouldWarn = true;
        message =
          "Your PayPal email is not yet confirmed. Deleting this payment method will require you to set up a new PayPal account connection.";
      }
    }

    if (shouldWarn) {
      setWarningMessage(message);
      setShowWarningDialog(true);
    } else {
      setShowWarningDialog(false);
    }
  }, [paymentAccountDetails, syncStatusDetails]);

  const deletePaymentMethodFromBackend = async () => {
    setIsDeleting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}user/delete_chef_account`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + getToken(),
          },
          body: JSON.stringify({ user_id: userId }),
        }
      );

      let data = {};
      try {
        data = await res.json();
      } catch (jsonErr) {
        console.error("Error parsing JSON response:", jsonErr);
        toast.error("Error processing server response.");
        return;
      }

      if (res.ok && data.status === true) {
        toast.success(
          "Your PayPal Account has been disconnected from Chef Republic."
        );
        setShowDeleteDialog(false);
        setShowWarningDialog(false);
        if (onPaymentDeleted) onPaymentDeleted();
      } else {
        console.error("Failed to delete payment method!", data?.message);
        toast.error(data?.message || "Failed to delete payment method!");
      }
    } catch (err) {
      console.error("Delete payment method error:", err);
      toast.error("Failed to delete payment method. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    if (showWarningDialog) {
      // Keep warning open, do not open delete dialog yet
      return;
    }
    setShowDeleteDialog(true);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <button
        type="button"
        className={className || "btn btn-orange rounded-pill mb-3"}
        disabled={!isPaymentLinked || (syncStatusDetails && syncStatusDetails.success === false)}
        onClick={handleDeleteClick}
        style={{
          boxSizing: "border-box",
          maxWidth: "100%",
          width: "100%",
          letterSpacing: "0.5px",
          backgroundColor: !isPaymentLinked || (syncStatusDetails && syncStatusDetails.success === false) ? "#cccbcbff" : "#FF5E41",
          color: "#fff",
          opacity: 1,
          cursor: !isPaymentLinked || (syncStatusDetails && syncStatusDetails.success === false) ? "not-allowed" : "pointer",
          ...style,
        }}
      >
        Delete Payment Method
      </button>

      {/* Warning Dialog – shown when account is not fully ready */}
      {/* <Dialog
        open={showWarningDialog}
        onClose={() => setShowWarningDialog(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Account Setup Incomplete
            </Typography>
            <IconButton
              onClick={() => setShowWarningDialog(false)}
              sx={{ color: "#666" }}
            >
              ✕
            </IconButton>
          </Box>

          <Typography variant="body1" sx={{ mb: 3, color: "#666" }}>
            {warningMessage}
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <button
              onClick={() => {
                setShowWarningDialog(false);
                setShowDeleteDialog(true);
              }}
              className="btn btn-orange rounded-pill"
              style={{ width: "100%" }}
            >
              Continue to Delete
            </button>
            <button
              onClick={() => setShowWarningDialog(false)}
              className="btn btn-outline-secondary rounded-pill"
              style={{
                width: "100%",
                border: "1px solid #ddd",
                color: "#666",
                backgroundColor: "#fff",
              }}
            >
              Cancel
            </button>
          </Box>
        </Box>
      </Dialog> */}

      {/* Delete Confirmation Dialog */}
     <Dialog
  open={showDeleteDialog}
  onClose={() => !isDeleting && setShowDeleteDialog(false)}
  PaperProps={{
    sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
  }}
>
  <Box sx={{ p: 3 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Delete Payment Method
      </Typography>
      <IconButton
        onClick={() => setShowDeleteDialog(false)}
        disabled={isDeleting}
        sx={{ color: "#666" }}
      >
        ✕
      </IconButton>
    </Box>

    <Typography variant="body1" sx={{ mb: 2, color: "#666" }}>
      Disconnecting your PayPal account will prevent you from offering PayPal
      services and products on Chef Republic.
    </Typography>

    <Typography variant="body1" sx={{ mb: 3, color: "#666" }}>
      Do you wish to continue?
    </Typography>

    <Box sx={{ display: "flex", gap: 2 }}>
      <button
        disabled={isDeleting}
        onClick={deletePaymentMethodFromBackend}
        className="btn btn-orange rounded-pill"
        style={{
          width: "100%",
          opacity: isDeleting ? 0.6 : 1,
          cursor: isDeleting ? "not-allowed" : "pointer",
        }}
      >
        {isDeleting ? "Deleting..." : "Yes, Delete"}
      </button>
    </Box>
  </Box>
</Dialog>
    </Box>
  );
}